import React from "react";
import { PropertyContext } from "../pages/PropertyContext.web";
import { isTeamsApp } from "../utils/url";
import { useFlightContext } from "./useFlightContext";

// see https://office.visualstudio.com/OC/_git/ShellServices?path=%2Fsources%2FShellService%2Fsrc%2FUserExperience.cs&_a=contents&version=GBmaster
export type LicenseType =
  /**
   * Consumer User has a license that allows access to both Copilot and Office, known as Copilot Pro.
   */
  | "Copilot"

  /**
   * Consumer User has a Copilot license they can use with Office, if they also have an Office license like Family or Personal.
   */
  | "CopilotInM365"

  /**
   * Consumer User has Family subscription of Office but also needs CopilotInM365 license to use Copilot.
   */
  | "Family"

  /**
   * Consumer User has Personal subscription of Office but also needs CopilotInM365 license to use Copilot.
   */
  | "Personal"

  /**
   * Enterprise user has a license that allows access to Copilot if they also have a license for Office, like OfficeProPlus.
   */
  | "CopilotAI"

  /**
   * Enterprise user has a license that allows access to BizChat. Use Eligibility API instead.
   */
  | "CopilotBusinessChat"
  | "CopilotIntelligentSearch"
  | "CopilotTeams"

  /**
   * Enterprise user has a license to Office, but also needs a license to use Copilot in Office, like CopilotAI.
   */
  | "OfficeProPlus"

  /**
   * Enterprise user a license to Office WAC aka Office Online aka Office Web Apps, but also needs a license to use Copilot in Office, like CopilotAI.
   */
  | "OfficeWeb"

  /**
   * Enterprise user has a license to Teams.
   */
  | "Teams";

export const useFeatureEnablement = () => {
  const { isFlightActive } = useFlightContext();
  const [siteSetting] = React.useContext(PropertyContext);

  const includesAny = React.useCallback(
    (allLicenses: string[] | undefined, checkForAny: LicenseType[]) => {
      if (!allLicenses) {
        return false;
      }

      const allLicensesHashed = new Set(allLicenses);
      for (const checkFor of checkForAny) {
        if (allLicensesHashed.has(checkFor)) {
          return true;
        }
      }
      return false;
    },
    []
  );

  return {
    isTryInOnAuthed: isFlightActive("isTryInEnabled") && !!siteSetting.user,
    isTryInRetrieveWorkingFiles:
      isFlightActive("isTryInEnabled") &&
      !!siteSetting.user &&
      isFlightActive("isTryInOfficeEnabled") &&
      includesAny(siteSetting.licenses, ["CopilotAI"]),
    includesAny,
    isWorkgroupPublishingEnabled:
      isFlightActive("isWorkgroupPublishingEnabled") &&
      !!siteSetting.user &&
      siteSetting.user.type === "AAD",
    isUseAccessUrlEnabled: isFlightActive("isUseAccessUrlEnabled"),
    isUseNewHandoffLoadingWindowEnabled:
      isFlightActive("isUseNewHandoffLoadingWindowEnabled") &&
      (!isTeamsApp() || !isFlightActive("isBizChatTryInTeamsAppEnabled")),
    isBizChatTryInTeamsAppEnabled: isFlightActive(
      "isBizChatTryInTeamsAppEnabled"
    ),
    isShowAppLinkEnabledSignedInUser:
      isFlightActive("isPromptAppLinkEnabled") &&
      !!siteSetting.user &&
      siteSetting.user.type === "AAD" &&
      includesAny(siteSetting.licenses, ["CopilotBusinessChat", "CopilotAI"]),
    isShowAppLinkEnabledNotSignedIn:
      isFlightActive("isPromptAppLinkEnabled") && !siteSetting.user,
  };
};
